









import { Component, Mixins, Prop, Inject } from "vue-property-decorator";
import { CreateElement, VNode } from "vue";
import { ITemplate, ITemplateCategory } from "@/models/templates";
import {
  ITemplateProvider,
  ITemplateProviderMethods
} from "@/models/providers/templates";
import { UpmindObjectTypes } from "@/data/enums/objects";

// Components
import CategorySelectField from "@/components/app/global/templates/templateFields/categorySelectField.vue";
import CodeField from "@/components/app/global/templates/templateFields/codeField.vue";
import HasValidation from "@/mixins/HasValidation";
import NameField from "@/components/app/global/templates/templateFields/nameField.vue";
import TemplateContent from "@/components/app/global/templates/contentForm.vue";
import ActionContentsTable from "@/components/app/admin/automation/actionContentsTable.vue";

type ClientAreaTemplateForm = Partial<
  Pick<ITemplate, "code" | "name" | "brand_id" | "body" | "type"> & {
    category_code: ITemplateCategory["code"];
  }
>;

@Component
export default class ClientTemplateForm extends Mixins(HasValidation) {
  // Inject

  @Inject({ from: "templateProvider" })
  public templateProvider!: () => ITemplateProvider;

  @Inject({ from: "templateProviderMethods" })
  public $templateMethods!: ITemplateProviderMethods;

  // Prop

  @Prop({ default: null })
  public categoryCode!: UpmindObjectTypes;

  @Prop({ default: true })
  public showCategory!: boolean;

  @Prop({ default: true })
  public showCode!: boolean;

  @Prop({ default: true })
  public showBodyArgsAndDesc!: boolean[];

  // Data

  public form: ClientAreaTemplateForm = {};
  public initialForm: ClientAreaTemplateForm = {};

  // Life-cycle methods

  public created() {
    this.form = this.initForm();
  }

  // Computed

  public get isFormChanged(): boolean {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  public get $templateData() {
    return this.templateProvider();
  }

  public get template() {
    return this.$templateData.template;
  }

  public get selectedCategory() {
    return this.$templateData.categories.find(
      i => i.code === this.form.category_code
    );
  }

  public get availableCategoriesForContent() {
    const brandCategory = this.$templateData.categories.find(
      i => i.code === "brand"
    );
    return this.$_.uniqBy(
      this.$_.compact([
        brandCategory,
        this.form.category_code ? this.selectedCategory : null
      ]),
      "code"
    );
  }

  // Methods

  public revertToInitialState() {
    this.form = this.initForm();
  }

  public initForm(): ClientAreaTemplateForm {
    return {
      body: this.template?.body,
      brand_id: this.template?.brand_id,
      category_code: this.template?.category?.code || this.categoryCode,
      code: this.template?.code,
      name: this.template?.name,
      type: this.$templateData.templateType
    };
  }

  public async validateBeforeSubmit() {
    try {
      this.$templateMethods.setIsProcessing(true);
      const isValid = await this.validate();
      if (!isValid) throw this.$t("_sentence.form_is_not_valid");

      const template = this.$templateData.isNew
        ? await this.$templateMethods.createTemplate(this.form)
        : await this.$templateMethods.updateTemplate(this.form);
      this.$emit("success", template);

      this.$buefy.toast.open({
        message: this.$t("_sentence.successfully_saved") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$templateMethods.setIsProcessing(false);
    }
  }

  public renderCategories(h: CreateElement) {
    if (!this.showCategory) return null;
    return h(CategorySelectField, {
      props: {
        value: this.form.category_code,
        categories: this.$templateData.categories,
        isDisabled:
          this.$templateData.isReadOnly ||
          this.$templateData.isProcessing ||
          this.$templateData.isLoadingCategories ||
          !this.$templateData.isNew ||
          !!this.categoryCode,
        isLoading:
          this.$templateData.isLoading || this.$templateData.isLoadingCategories
      },
      on: { input: e => (this.form.category_code = e) }
    });
  }

  public renderTemplateContent(h: CreateElement) {
    return h(TemplateContent, {
      props: {
        body: this.form.body,
        categories: this.availableCategoriesForContent,
        category: this.selectedCategory,
        processing: this.$templateData.isProcessing,
        showSubject: false,
        template: this.$templateData.template,
        codeMirrorOptions: {
          mode: "twig",
          lineNumbers: true,
          styleActiveLine: true
        },
        isDisabled: this.$templateData.isReadOnly,
        showBodyArgsAndDesc: this.showBodyArgsAndDesc
      },
      on: {
        "update:body": e => (this.form.body = e)
      }
    });
  }

  public renderName(h: CreateElement) {
    return h(NameField, {
      props: {
        value: this.form.name,
        isDisabled: this.$templateData.isReadOnly
      },
      attrs: { rules: "required" },
      on: { input: e => (this.form.name = e) }
    });
  }

  public renderCode(h: CreateElement) {
    if (!this.showCode) return null;
    return h(CodeField, {
      props: { value: this.form.code, isDisabled: !this.$templateData.isNew },
      on: { input: e => (this.form.code = e) }
    });
  }

  public renderContextualise(
    defaultSlot: VNode | null,
    { label, description }
  ) {
    const h = this.$createElement;

    return h("contextualise", {
      attrs: { "min-width": "28rem" },
      props: { label },
      class: "has-margin-top-100",
      scopedSlots: {
        desc: () => h("span", description as string),
        default: () => defaultSlot
      }
    });
  }

  public renderTranslations(h: CreateElement) {
    if (this.$templateData.isNew || this.$templateData.isLoading) return null;
    return this.renderContextualise(
      h(ActionContentsTable, {
        props: {
          id: `CATC` /** Client Area Template */,
          categories: this.availableCategoriesForContent,
          template: this.$templateData.template,
          showSubject: false
        },
        on: {
          "contents-updated": async () => {
            this.$templateMethods.reloadContents();
          }
        }
      }),
      {
        label: this.$t("_.translations"),
        description: this.$t("_sentence.templates.translations_desc")
      }
    );
  }

  public get components() {
    return {
      render: (h: CreateElement) => {
        return h("div", {}, [
          this.renderName(h),
          this.renderCode(h),
          this.renderCategories(h),
          this.renderTemplateContent(h),
          this.renderTranslations(h)
        ]);
      }
    };
  }
}
